import { ActionIcon, Box, Button, Card, Center, CopyButton, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy, IconDownload } from '@tabler/icons-react';
import { JsonEditor } from 'json-edit-react';

import type { TestDefinition } from '@admin/hooks/request-logger/useTestGenerator';

interface ExportStepProps {
  exportedData: TestDefinition | null;
}

export const ExportStep = ({ exportedData }: ExportStepProps) => {
  const handleDownload = () => {
    if (!exportedData) return;

    const blob = new Blob([JSON.stringify(exportedData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-${exportedData.metadata.batchName || exportedData.metadata.batchId}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!exportedData) {
    return (
      <Stack gap="md" mt="md">
        <Center p="xl">
          <Stack align="center" gap="md">
            <Loader size="md" />
            <Text size="sm" c="dimmed">
              Generating test definition...
            </Text>
          </Stack>
        </Center>
      </Stack>
    );
  }

  return (
    <Stack gap="md" mt="md">
      <Card shadow="sm" padding="md" radius="md" withBorder>
        <Group justify="space-between">
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Test Definition Ready
            </Text>
            <Group gap="md">
              <Text size="xs" c="dimmed">
                Batch: {exportedData.metadata.batchName}
              </Text>
              <Text size="xs" c="dimmed">
                Steps: {exportedData.steps.length}
              </Text>
              <Text size="xs" c="dimmed">
                Generated: {new Date(exportedData.metadata.generatedAt).toLocaleString()}
              </Text>
            </Group>
          </Stack>
          <Group>
            <CopyButton value={JSON.stringify(exportedData, null, 2)}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <ActionIcon color={copied ? 'teal' : 'gray'} variant="light" onClick={copy}>
                    {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
            <Button leftSection={<IconDownload size={16} />} onClick={handleDownload}>
              Download JSON
            </Button>
          </Group>
        </Group>
      </Card>

      <Box style={{ maxHeight: '500px', overflow: 'auto' }}>
        <JsonEditor data={exportedData} restrictEdit restrictDelete restrictAdd restrictDrag minWidth="100%" />
      </Box>
    </Stack>
  );
};
